import React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Checkbox from "@mui/joy/Checkbox";
import Chip from "@mui/joy/Chip";
import CircularProgress from "@mui/joy/CircularProgress";
import FormControl from "@mui/joy/FormControl";
import FormHelperText from "@mui/joy/FormHelperText";
import FormLabel from "@mui/joy/FormLabel";
import IconButton from "@mui/joy/IconButton";
import Input from "@mui/joy/Input";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import ModalDialog from "@mui/joy/ModalDialog";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import Table from "@mui/joy/Table";
import Tooltip from "@mui/joy/Tooltip";
import Typography from "@mui/joy/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import RefreshIcon from "@mui/icons-material/Refresh";
import ConfirmDialog from "../../../components/confirmDialog/ConfirmDialog";
import { NotificationService } from "../../../common/notification";
import { formatUtcDateTime } from "../../../common/dateTime";
import {
    AdminExternalAccountInfo,
    AdminExternalProviderInfo,
    ExternalAccountStateDescription,
    ExternalProxiesSummary,
    PurchaseApi,
} from "../../../models/externalMarkets";
import {
    extAccountCheckApi,
    extAccountCreateApi,
    extAccountDeleteApi,
    extAccountResetCooldownApi,
    extAccountRotateProxyApi,
    extAccountsApi,
    extAccountUpdateApi,
    extProxiesSummaryApi,
} from "../externalMarketsApi";
import { formatUsd } from "../components/shared";

type Props = {
    provider: AdminExternalProviderInfo;
};

export function AccountsTab(props: Props) {
    const { provider } = props;
    const [accounts, setAccounts] = React.useState<AdminExternalAccountInfo[]>([]);
    const [proxies, setProxies] = React.useState<ExternalProxiesSummary | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [busy, setBusy] = React.useState<string | null>(null);
    const [editing, setEditing] = React.useState<AdminExternalAccountInfo | "new" | null>(null);
    const [deleting, setDeleting] = React.useState<AdminExternalAccountInfo | null>(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const [accountsRes, proxiesRes] = await Promise.all([
                extAccountsApi(provider.key),
                extProxiesSummaryApi(provider.key),
            ]);
            if (accountsRes.result) {
                setAccounts(accountsRes.value ?? []);
            } else {
                NotificationService.alertError(accountsRes.error ?? "Не удалось загрузить аккаунты");
            }
            setProxies(proxiesRes.result ? proxiesRes.value ?? null : null);
        } finally {
            setLoading(false);
        }
    }, [provider.key]);

    React.useEffect(() => {
        load().catch(console.error);
    }, [load]);

    const run = async (key: string, action: () => Promise<{ result: boolean; error?: string }>, success: string) => {
        setBusy(key);
        try {
            const res = await action();
            if (res.result) {
                NotificationService.alertSuccess(success);
                await load();
            } else {
                NotificationService.alertError(res.error ?? "Операция не выполнена");
            }
        } finally {
            setBusy(null);
        }
    };

    const check = async (a: AdminExternalAccountInfo) => {
        setBusy(`check-${a.id}`);
        try {
            const res = await extAccountCheckApi(provider.key, a.id);
            if (!res.result || !res.value) {
                NotificationService.alertError(res.error ?? "Проверка не выполнена");
                return;
            }
            const r = res.value;
            const proxy =
                r.proxyOk === undefined || r.proxyOk === null
                    ? "прокси нет, запросы идут напрямую"
                    : r.proxyOk
                      ? `прокси #${r.proxyId} отвечает`
                      : `прокси #${r.proxyId} не отвечает`;
            if (r.keyValid) {
                NotificationService.alertSuccess(`Ключ рабочий, баланс ${formatUsd(r.balanceUsd)}; ${proxy}.`, a.label);
            } else {
                NotificationService.alertError(`Ключ не прошёл проверку: ${r.keyError ?? "без описания"}; ${proxy}.`, a.label);
            }
            await load();
        } finally {
            setBusy(null);
        }
    };

    const healthy = accounts.filter((a) => a.state === "healthy").length;

    return (
        <Stack spacing={1.5}>
            <Box
                sx={{
                    display: "flex",
                    gap: 1.5,
                    flexWrap: "wrap",
                    alignItems: "center",
                    p: 2,
                    bgcolor: "background.level1",
                    borderRadius: "md",
                }}
            >
                <Chip variant="soft" color={healthy >= 2 ? "success" : "warning"}>
                    Здоровых аккаунтов: {healthy} из {accounts.filter((a) => a.isActive).length} активных
                </Chip>
                {proxies && (
                    <Tooltip
                        variant="soft"
                        title="Каждому активному аккаунту нужен свой рабочий прокси типа «Поставщики». Меньше прокси — меньше параллельных запросов к поставщику."
                    >
                        <Chip variant="soft" color={proxies.total - proxies.failed >= proxies.activeAccounts ? "success" : "warning"}>
                            Прокси: {proxies.total} всего, {proxies.failed} нерабочих, {proxies.free} свободных, аккаунтов{" "}
                            {proxies.activeAccounts}
                        </Chip>
                    </Tooltip>
                )}
                <Box sx={{ flex: 1 }} />
                <IconButton size="sm" variant="outlined" onClick={() => load()} disabled={loading}>
                    <RefreshIcon />
                </IconButton>
                <Button size="sm" startDecorator={<AddIcon />} onClick={() => setEditing("new")}>
                    Добавить аккаунт
                </Button>
            </Box>

            {loading && accounts.length === 0 ? (
                <CircularProgress size="sm" />
            ) : accounts.length === 0 ? (
                <Typography level="body-sm">Аккаунтов нет. Без аккаунта с правом чтения витрина не синхронизируется.</Typography>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: "sm", overflow: "auto" }}>
                    <Table size="sm" hoverRow sx={{ minWidth: 1100 }}>
                        <thead>
                            <tr>
                                <th style={{ width: 50 }}>#</th>
                                <th>Метка</th>
                                <th>Ключ</th>
                                <th>Роли</th>
                                <th>Состояние</th>
                                <th style={{ textAlign: "right" }}>Баланс</th>
                                <th>Прокси</th>
                                <th>Последнее использование</th>
                                <th style={{ width: 190 }}>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {accounts.map((a) => {
                                const state = ExternalAccountStateDescription[a.state] ?? { label: a.state, color: "neutral" as const };
                                return (
                                    <tr key={a.id}>
                                        <td>{a.id}</td>
                                        <td>
                                            <Typography level="title-sm">{a.label}</Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-xs" sx={{ fontFamily: "monospace" }}>
                                                {a.apiKeyMask}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Stack direction="row" spacing={0.5}>
                                                {a.canRead && <Chip size="sm" variant="outlined">чтение</Chip>}
                                                {a.canPurchase && (
                                                    <Chip size="sm" variant="outlined" color="primary">
                                                        {a.purchaseApi === "Project" ? `проект #${a.projectId ?? "?"}` : "reseller"}
                                                    </Chip>
                                                )}
                                            </Stack>
                                        </td>
                                        <td>
                                            <Chip size="sm" variant="soft" color={state.color}>
                                                {state.label}
                                            </Chip>
                                            {a.state === "cooldown" && a.cooldownUntil && (
                                                <Typography level="body-xs">до {formatUtcDateTime(a.cooldownUntil)}</Typography>
                                            )}
                                            {a.lastError && (
                                                <Tooltip title={a.lastError} variant="soft" sx={{ maxWidth: 480 }}>
                                                    <Typography
                                                        level="body-xs"
                                                        color="danger"
                                                        sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                                    >
                                                        {a.lastError}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                        </td>
                                        <td style={{ textAlign: "right" }}>{formatUsd(a.lastBalanceUsd)}</td>
                                        <td>
                                            {a.proxy ? (
                                                <>
                                                    <Typography level="body-xs" color={a.proxy.isFailed ? "danger" : undefined}>
                                                        #{a.proxy.id} {a.proxy.address}
                                                    </Typography>
                                                    {a.proxy.isFailed && (
                                                        <Typography level="body-xs" color="danger">
                                                            нерабочий{a.proxy.failureReason ? `: ${a.proxy.failureReason}` : ""}
                                                        </Typography>
                                                    )}
                                                </>
                                            ) : (
                                                <Typography level="body-xs">напрямую</Typography>
                                            )}
                                        </td>
                                        <td>
                                            <Typography level="body-xs">{formatUtcDateTime(a.lastUsedAt)}</Typography>
                                        </td>
                                        <td>
                                            <Stack direction="row" spacing={0.5}>
                                                <Tooltip title="Проверить ключ и прокси">
                                                    <IconButton
                                                        size="sm"
                                                        variant="outlined"
                                                        loading={busy === `check-${a.id}`}
                                                        onClick={() => check(a)}
                                                    >
                                                        <FactCheckIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Снять остывание">
                                                    <span>
                                                        <IconButton
                                                            size="sm"
                                                            variant="outlined"
                                                            disabled={a.state !== "cooldown"}
                                                            loading={busy === `cool-${a.id}`}
                                                            onClick={() =>
                                                                run(
                                                                    `cool-${a.id}`,
                                                                    () => extAccountResetCooldownApi(provider.key, a.id),
                                                                    "Остывание снято"
                                                                )
                                                            }
                                                        >
                                                            <AcUnitIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Сменить прокси: текущий помечается нерабочим">
                                                    <span>
                                                        <IconButton
                                                            size="sm"
                                                            variant="outlined"
                                                            disabled={!a.proxy}
                                                            loading={busy === `proxy-${a.id}`}
                                                            onClick={() =>
                                                                run(
                                                                    `proxy-${a.id}`,
                                                                    () => extAccountRotateProxyApi(provider.key, a.id),
                                                                    "Прокси помечен нерабочим, аккаунт получит новый при ближайшей перепроверке"
                                                                )
                                                            }
                                                        >
                                                            <SwapHorizIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Изменить">
                                                    <IconButton size="sm" variant="outlined" onClick={() => setEditing(a)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Удалить">
                                                    <IconButton size="sm" variant="outlined" color="danger" onClick={() => setDeleting(a)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            <AccountDialog
                account={editing}
                onClose={() => setEditing(null)}
                onSave={async (form) => {
                    const purchase = {
                        purchaseApi: form.purchaseApi,
                        projectId: form.purchaseApi === "Project" ? Number(form.projectId.trim()) : undefined,
                    };
                    const res =
                        editing === "new"
                            ? await extAccountCreateApi(provider.key, {
                                  label: form.label,
                                  apiKey: form.apiKey,
                                  canRead: form.canRead,
                                  canPurchase: form.canPurchase,
                                  ...purchase,
                              })
                            : await extAccountUpdateApi(provider.key, (editing as AdminExternalAccountInfo).id, {
                                  label: form.label,
                                  apiKey: form.apiKey,
                                  canRead: form.canRead,
                                  canPurchase: form.canPurchase,
                                  isActive: form.isActive,
                                  ...purchase,
                              });
                    if (res.result) {
                        NotificationService.alertSuccess(editing === "new" ? "Аккаунт добавлен" : "Аккаунт сохранён");
                        setEditing(null);
                        await load();
                    } else {
                        NotificationService.alertError(res.error ?? "Не удалось сохранить аккаунт");
                    }
                }}
            />

            <ConfirmDialog
                title="Удаление аккаунта"
                open={deleting !== null}
                setOpen={(open) => !open && setDeleting(null)}
                onConfirm={async () => {
                    if (!deleting) {
                        return;
                    }
                    const res = await extAccountDeleteApi(provider.key, deleting.id);
                    if (res.result) {
                        NotificationService.alertSuccess("Аккаунт удалён");
                        await load();
                    } else {
                        NotificationService.alertError(res.error ?? "Не удалось удалить аккаунт");
                    }
                }}
            >
                {`Удалить аккаунт «${deleting?.label ?? ""}»? Аккаунт с историей выкупов удалить нельзя — его выключают.`}
            </ConfirmDialog>
        </Stack>
    );
}

type AccountForm = {
    label: string;
    apiKey: string;
    canRead: boolean;
    canPurchase: boolean;
    purchaseApi: PurchaseApi;
    projectId: string;
    isActive: boolean;
};

const EMPTY_FORM: AccountForm = {
    label: "",
    apiKey: "",
    canRead: true,
    canPurchase: false,
    purchaseApi: "Reseller",
    projectId: "",
    isActive: true,
};

function isPositiveInteger(value: string): boolean {
    return /^\d+$/.test(value.trim()) && Number(value) > 0;
}

function AccountDialog(props: {
    account: AdminExternalAccountInfo | "new" | null;
    onClose: () => void;
    onSave: (form: AccountForm) => Promise<void>;
}) {
    const { account, onClose, onSave } = props;
    const isNew = account === "new";
    const [form, setForm] = React.useState<AccountForm>(EMPTY_FORM);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (account === "new") {
            setForm(EMPTY_FORM);
        } else if (account) {
            setForm({
                label: account.label,
                apiKey: "",
                canRead: account.canRead,
                canPurchase: account.canPurchase,
                purchaseApi: account.purchaseApi === "Project" ? "Project" : "Reseller",
                projectId: account.projectId?.toString() ?? "",
                isActive: account.isActive,
            });
        }
    }, [account]);

    const projectIdInvalid = form.purchaseApi === "Project" && !isPositiveInteger(form.projectId);
    const invalid =
        !form.label.trim() || (isNew && !form.apiKey.trim()) || (!form.canRead && !form.canPurchase) || projectIdInvalid;

    return (
        <Modal open={account !== null} onClose={() => !saving && onClose()}>
            <ModalDialog sx={{ width: { xs: "100%", sm: 480 } }}>
                <ModalClose disabled={saving} />
                <Typography level="h4">{isNew ? "Новый аккаунт" : `Аккаунт «${(account as AdminExternalAccountInfo)?.label ?? ""}»`}</Typography>
                <FormControl size="sm">
                    <FormLabel>Метка</FormLabel>
                    <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
                    <FormHelperText>Уникальна в пределах поставщика, видна в логах и метриках</FormHelperText>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Ключ API</FormLabel>
                    <Input
                        type="password"
                        autoComplete="new-password"
                        value={form.apiKey}
                        placeholder={isNew ? "" : "не менять"}
                        onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                    />
                    <FormHelperText>
                        {isNew ? "Ключ проверяется запросом к поставщику перед сохранением" : "Пусто — ключ не меняется"}
                    </FormHelperText>
                </FormControl>
                <Stack direction="row" spacing={2}>
                    <Checkbox
                        label="Чтение витрины"
                        checked={form.canRead}
                        onChange={(e) => setForm({ ...form, canRead: e.target.checked })}
                    />
                    <Checkbox
                        label="Покупка"
                        checked={form.canPurchase}
                        onChange={(e) => setForm({ ...form, canPurchase: e.target.checked })}
                    />
                    {!isNew && (
                        <Checkbox
                            label="Включён"
                            checked={form.isActive}
                            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                        />
                    )}
                </Stack>
                <Stack direction="row" spacing={2}>
                    <FormControl size="sm" sx={{ flex: 1 }}>
                        <FormLabel>API выкупа</FormLabel>
                        <Select value={form.purchaseApi} onChange={(_, v) => v && setForm({ ...form, purchaseApi: v })}>
                            <Option value="Reseller">Ресейлер</Option>
                            <Option value="Project">Проект</Option>
                        </Select>
                    </FormControl>
                    {form.purchaseApi === "Project" && (
                        <FormControl size="sm" sx={{ flex: 1 }} error={projectIdInvalid}>
                            <FormLabel>ID проекта</FormLabel>
                            <Input
                                inputMode="numeric"
                                value={form.projectId}
                                onChange={(e) => setForm({ ...form, projectId: e.target.value })}
                            />
                            <FormHelperText>
                                {projectIdInvalid ? "Целое положительное число" : "Из настроек проекта на сайте поставщика"}
                            </FormHelperText>
                        </FormControl>
                    )}
                </Stack>
                <FormHelperText>
                    Ресейлер выкупает одним запросом resell — у ключа должна быть роль Reseller. Проект выкупает через API
                    проекта с указанным ID. Если поставщик откажет ключу в праве на выкуп, право покупки снимется
                    автоматически (причина — в последней ошибке аккаунта); чтение витрины продолжится. Если покупающих
                    аккаунтов не осталось, связки поставщика выключаются и витрина уходит на паузу — включить обратно
                    вручную. Проверить право на выкуп заранее нельзя: API его не отдаёт.
                </FormHelperText>
                {!form.canRead && !form.canPurchase && (
                    <Typography level="body-xs" color="danger">
                        Нужна хотя бы одна роль
                    </Typography>
                )}
                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    <Button variant="plain" color="neutral" disabled={saving} onClick={onClose}>
                        Отмена
                    </Button>
                    <Button
                        loading={saving}
                        disabled={invalid}
                        onClick={async () => {
                            setSaving(true);
                            try {
                                await onSave({ ...form, label: form.label.trim() });
                            } finally {
                                setSaving(false);
                            }
                        }}
                    >
                        Сохранить
                    </Button>
                </Box>
            </ModalDialog>
        </Modal>
    );
}
